// Logic related to brand menu
import { CartItemAdditionTypes } from '../constants/event';
import { ModalityType } from '../generated-interfaces/graphql';
import { setAutoCombinedCart } from '../redux/features/autoCombo/autoCombo.slice';
import {
  IBrandMenuOrderConstraint,
  IBrandMenuOrderItem,
} from '../types/brandMenu';
import { selectModifierInCartItem, CartItem } from '../utils/cart';
import logger from '../utils/logger';
import { ModSymbolCodeNameMappingType } from '../utils/mappings';
import { TopLevelMenuItem, splitPathToItem as splitPath } from '../utils/menu';
import { buildFullMenuUnifiedItem } from '../utils/menuUnification.utils';
import { GenericMap } from '../utils/types';
import { setSequenceId } from './cartSlice';
import { dialogActions, selectItemAndResetNavigation } from './dialogSlice';

function compareConstraintArrays(
  arr1: IBrandMenuOrderConstraint[],
  arr2: IBrandMenuOrderConstraint[]
): boolean {
  // Helper function to clean an object by removing specified keys and empty constraints
  function cleanObject(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map(cleanObject);
    }

    if (obj && typeof obj === 'object') {
      const cleaned = {};

      for (const [key, value] of Object.entries(obj)) {
        // Skip the keys we want to ignore
        if (key === 'added_by' || key === 'is_auto_combined') {
          continue;
        }

        // Skip constraints if it's an empty array or undefined
        if (
          key === 'constraints' &&
          ((Array.isArray(value) && value.length === 0) || !value)
        ) {
          continue;
        }

        // Recursively clean nested objects/arrays
        (cleaned as Record<string, any>)[key] = cleanObject(value);
      }

      return cleaned;
    }

    return obj;
  }

  // Helper function to perform deep comparison
  function deepEqual(obj1: any, obj2: any): boolean {
    if (obj1 === obj2) return true;

    if (obj1 == null || obj2 == null) return obj1 === obj2;

    if (Array.isArray(obj1) && Array.isArray(obj2)) {
      if (obj1.length !== obj2.length) return false;
      return obj1.every((item, index) => deepEqual(item, obj2[index]));
    }

    if (typeof obj1 === 'object' && typeof obj2 === 'object') {
      const keys1 = Object.keys(obj1);
      const keys2 = Object.keys(obj2);

      if (keys1.length !== keys2.length) return false;

      return keys1.every(
        (key: string) => keys2.includes(key) && deepEqual(obj1[key], obj2[key])
      );
    }

    return false;
  }

  // Clean both arrays
  const cleanedArr1 = cleanObject(arr1);
  const cleanedArr2 = cleanObject(arr2);

  // Compare the cleaned arrays
  return deepEqual(cleanedArr1, cleanedArr2);
}

export const findNodeInCartItemTree = ({
  item,
  pathToItem,
  cartItems,
}: {
  item: string;
  pathToItem: string;
  cartItems: GenericMap<CartItem>;
}) => {
  let matchingCartItem: CartItem | undefined;
  const pathSegments = splitPath(pathToItem);

  if (pathSegments.length === 1) {
    matchingCartItem = Object.values(cartItems).find(
      (cartItem: CartItem) => cartItem.name === item
    );
  } else if (pathSegments.length > 2) {
    const parentItem: CartItem | undefined = Object.values(cartItems).find(
      (cartItem: CartItem) => cartItem.name === pathSegments[1]
    );
    if (parentItem) {
      matchingCartItem = Object.values(
        parentItem?.childModifierGroups[pathSegments[2]]?.selectedItems
      ).find((cartItem: CartItem) => cartItem.name === item);
    }
  }

  return !!matchingCartItem?.readback;
};

export const recursivelyFillMUModifiers = ({
  node,
  constraints,
  modSymbolMapping,
  modality,
  pathToItem,
  cartItems,
  combinedConstraintsCount,
}: {
  node: CartItem;
  constraints: IBrandMenuOrderConstraint[];
  modSymbolMapping: ModSymbolCodeNameMappingType;
  modality: ModalityType;
  pathToItem: string;
  cartItems: GenericMap<CartItem>;
  combinedConstraintsCount: Record<string, number>;
}) => {
  constraints?.forEach((constraint: IBrandMenuOrderConstraint) => {
    const { constraint_name: constraintName, items: constraintItems } =
      constraint || {};

    const newPathToItem = [
      ...(pathToItem ? [pathToItem] : []),
      constraintName,
    ].join('__');

    constraintItems?.forEach((constraintItem: IBrandMenuOrderItem) => {
      const {
        item,
        quantity = 1,
        constraints: innerConstraints,
      } = constraintItem || {};

      const canReadback = findNodeInCartItemTree({
        item,
        pathToItem: newPathToItem,
        cartItems,
      });

      combinedConstraintsCount[item] =
        (combinedConstraintsCount[item] || 0) + quantity;

      selectModifierInCartItem(
        node,
        item,
        constraintName,
        true,
        '',
        modSymbolMapping,
        {},
        modality,
        newPathToItem,
        '',
        canReadback,
        quantity
      );

      if (innerConstraints?.length) {
        recursivelyFillMUModifiers({
          node,
          constraints: innerConstraints,
          modSymbolMapping,
          modality,
          pathToItem: `${newPathToItem}__${item}`,
          cartItems,
          combinedConstraintsCount,
        });
      }
    });
  });
};

export const areSameConstraints = (
  entryConstraints: IBrandMenuOrderConstraint[],
  menuItemConstraints: IBrandMenuOrderConstraint[]
) => {
  if (entryConstraints.length !== menuItemConstraints.length) return false;

  return entryConstraints.every(
    (entryConstraint: IBrandMenuOrderConstraint): boolean => {
      const {
        constraint_name: entryConstraintName,
        items: entryConstraintItems,
      } = entryConstraint || {};

      const menuItemConstraint = menuItemConstraints.find(
        (menuItemConstraint: IBrandMenuOrderConstraint) =>
          menuItemConstraint.constraint_name === entryConstraintName
      );

      if (!menuItemConstraint) return false;

      const { items: menuItemConstraintItems } = menuItemConstraint || {};

      return entryConstraintItems.every((entryItem: IBrandMenuOrderItem) => {
        return menuItemConstraintItems.some(
          (menuItem: IBrandMenuOrderItem) =>
            entryItem.item === menuItem.item &&
            ((!entryItem.constraints && !menuItem.constraints) ||
              (entryItem.constraints &&
                menuItem.constraints &&
                areSameConstraints(
                  entryItem.constraints,
                  menuItem.constraints
                )))
        );
      });
    }
  );
};

export const findBMEntryInPayload = (
  entry: IBrandMenuOrderItem,
  menuModuleItems: IBrandMenuOrderItem[],
  menuModuleItemsCartItemIds: number[],
  nonCombinedCartItemIds: number[]
) => {
  const { item: name, constraints: entryConstraints = [] } = entry;

  const matchingIndexes: number[] = [];
  menuModuleItems.forEach((menuItem: IBrandMenuOrderItem, index) => {
    const { item: menuItemName, constraints: menuItemConstraints = [] } =
      menuItem;

    if (name === menuItemName) {
      if (compareConstraintArrays(entryConstraints, menuItemConstraints)) {
        matchingIndexes.push(index);
      }
    }
    return false;
  });

  if (matchingIndexes.length) {
    let matchingIndex = -1;
    matchingIndexes.some((index) => {
      const cartItemId = menuModuleItemsCartItemIds[index];
      if (!nonCombinedCartItemIds.includes(cartItemId)) {
        matchingIndex = index;
        return true;
      }
      return false;
    });
    return matchingIndex;
  }
  return -1;
};

export const buildBMAutoCombinedItem = ({
  getState,
  bmRespEntry,
  currSequenceId,
  autoCombinedCartItems,
  autoCombinedCartItemsQuantity,
  combinedConstraintsCount,
}: {
  getState: Function;
  bmRespEntry: IBrandMenuOrderItem;
  currSequenceId: number;
  autoCombinedCartItems: GenericMap<CartItem>;
  autoCombinedCartItemsQuantity: Record<string, number>;
  combinedConstraintsCount: Record<string, number>;
}) => {
  const {
    cart: { cartItems, modality },
    menu: {
      topLevelMenuItems,
      brandMenu,
      unavailableItemsByName,
      modSymbolMapping,
      restaurantMealPeriodsByName,
    },
    restaurant: {
      selectedRestaurantDetails: { restaurantCode = '', timezone },
    },
  } = getState();

  const { item: name, quantity, constraints } = bmRespEntry;

  const menuItem = topLevelMenuItems[name];

  const parsedMenuItem = buildFullMenuUnifiedItem({
    item: menuItem,
    brandMenu,
    restaurantCode,
    unavailableItemsByName,
    buildViaAutoCombo: true,
    timezone,
    restaurantMealPeriodsByName,
  });

  const cartItem: CartItem = {
    ...parsedMenuItem,
    cartItemId: currSequenceId,
    childModifierGroups: {},
    modality,
    modcode: menuItem.modcode,
    addedBy: CartItemAdditionTypes.human,
    readback: true,
    isAutoCombined: true,
  };

  autoCombinedCartItems[currSequenceId] = cartItem;
  autoCombinedCartItemsQuantity[currSequenceId] = quantity;

  let node = cartItem;

  recursivelyFillMUModifiers({
    node,
    constraints: constraints || [],
    modSymbolMapping,
    modality,
    pathToItem: '',
    cartItems,
    combinedConstraintsCount,
  });
};

export const cleanAutoCombinedItems = ({
  combinedCartItemIds,
  autoCombinedCartItems,
  autoCombinedCartItemsQuantity,
}: {
  combinedCartItemIds: number[];
  autoCombinedCartItems: GenericMap<CartItem>;
  autoCombinedCartItemsQuantity: Record<string, number>;
}) => {
  combinedCartItemIds.forEach((cartItemId: Number) => {
    const cartItemString = String(cartItemId);

    const item = autoCombinedCartItems[cartItemString];
    if (item) {
      delete autoCombinedCartItems[cartItemString];
      delete autoCombinedCartItemsQuantity[cartItemString];
    }
  });
};

// Logic to check if the menu module response includes any non-top level items
export const isValidResponse = (
  bmData: IBrandMenuOrderItem[],
  topLevelMenuItems: GenericMap<TopLevelMenuItem>
) => {
  return bmData.every((bmRespEntry: IBrandMenuOrderItem) => {
    const { item: name } = bmRespEntry;
    const menuItem = topLevelMenuItems[name];

    if (!menuItem) return false;
    return true;
  });
};

export const fillMUAutoCombinedItems = ({
  dispatch,
  getState,
  bmData,
  menuModuleItems,
  menuModuleItemsCartItemIds,
}: {
  dispatch: Function;
  getState: Function;
  bmData: IBrandMenuOrderItem[];
  menuModuleItems: IBrandMenuOrderItem[];
  menuModuleItemsCartItemIds: number[];
}) => {
  const {
    cart: { cartItems, cartItemsQuantity, sequenceId },
    menu: { topLevelMenuItems },
    restaurant: {
      selectedRestaurantDetails: { restaurantCode = '' },
    },
    sessionBoundary: { sessionId },
    autoCombo: {
      autoCombinedCartItems: oldACItems,
      autoCombinedCartItemsQuantity: oldACItemsQty,
      combinedCartItemIds: oldCombinedCartItemIds,
    },
  } = getState();

  let currSequenceId = sequenceId;
  const combinedConstraintsCount: Record<string, number> = {};
  const nonCombinedCartItemIds: number[] = [];

  // Step-1: Update autoCombinedCartItems with initial cart
  const autoCombinedCartItems = Object.assign(
    {},
    Object.values(oldACItems).length ? oldACItems : cartItems
  );
  const autoCombinedCartItemsQuantity = Object.assign(
    {},
    Object.values(oldACItemsQty).length ? oldACItemsQty : cartItemsQuantity
  );

  // Step-2: Loop through the brand menu response and create auto-combined cart item accordingly
  bmData.forEach((bmRespEntry: IBrandMenuOrderItem) => {
    const { item: name } = bmRespEntry;
    const menuItem = topLevelMenuItems[name];

    const matchingIndex = findBMEntryInPayload(
      bmRespEntry,
      menuModuleItems,
      menuModuleItemsCartItemIds,
      nonCombinedCartItemIds
    );

    const isBMRespAutoCombined = bmRespEntry.is_auto_combined;
    const isOldAutoCombined = isBMRespAutoCombined
      ? false
      : matchingIndex > -1
      ? menuModuleItems[matchingIndex].is_auto_combined
      : false;

    if (menuItem) {
      if (isBMRespAutoCombined || isOldAutoCombined) {
        // Step-3: Build full autoCombinedMenuItem along with modifiers and add to autoCombinedCartItems
        buildBMAutoCombinedItem({
          getState,
          bmRespEntry,
          currSequenceId,
          autoCombinedCartItems,
          autoCombinedCartItemsQuantity,
          combinedConstraintsCount,
        });
        // Step-4: Increment sequence ID so that newly creating cartItem will not include duplicate cartItemId
        currSequenceId += 1;
        dispatch(setSequenceId(currSequenceId));
      } else {
        // Step-5: Get the cart item id which did not get auto-combined

        if (matchingIndex >= 0) {
          const cartItemId = menuModuleItemsCartItemIds[matchingIndex];
          if (
            bmRespEntry.quantity !== menuModuleItems[matchingIndex].quantity
          ) {
            autoCombinedCartItemsQuantity[cartItemId] = bmRespEntry.quantity;
          }
          if (!nonCombinedCartItemIds.includes(cartItemId)) {
            nonCombinedCartItemIds.push(cartItemId);
          }
        }
      }
    } else {
      logger.log({
        message: `can not find the ${name} in the top level items of brand menu`,
        sessionId,
        restaurantCode,
      });
    }
  });

  // Step-6: Keep the nonCombinedCartItemIds and remove the other ones
  const combinedCartItemIds = [
    ...oldCombinedCartItemIds,
    ...menuModuleItemsCartItemIds,
  ].filter((origId) => !nonCombinedCartItemIds.includes(origId));
  cleanAutoCombinedItems({
    combinedCartItemIds,
    autoCombinedCartItems,
    autoCombinedCartItemsQuantity,
  });

  // Step-7: Update final autoCombinedCartItems after cleaning up the items
  dispatch(
    setAutoCombinedCart({
      autoCombinedCartItems,
      autoCombinedCartItemsQuantity,
      combinedCartItemIds,
    })
  );

  // Step-9: Update the select item to be the regular cart item before auto combined item as it's not selectable
  const items: CartItem[] = Object.values(autoCombinedCartItems);
  const firstAutoCombinedCartItemIdx = items.findIndex(
    (item: CartItem) => item.isAutoCombined
  );

  if (firstAutoCombinedCartItemIdx) {
    const selectedItem = items[firstAutoCombinedCartItemIdx - 1];
    if (selectedItem) {
      dispatch(
        dialogActions.selectItemAndResetNavigation({
          item: items[firstAutoCombinedCartItemIdx - 1],
          itemCartId: selectedItem.cartItemId,
        })
      );
    }
  } else {
    dispatch(dialogActions.selectItemAndResetNavigation(undefined));
  }
};
